import { Timer } from 'lucide-react';

import { JobApplicationForm } from '@/modules/careers/components/jobApplicationForm/JobApplicationForm';
import { JobApplicationSubmitted } from '@/modules/careers/components/jobApplicationSubmitted/JobApplicationSubmitted';
import { useSubmitJobApplicationMutation } from '@/modules/careers/mutations/useSubmitJobApplicationMutation';
import type { CareersJobApi, CareersPageApi } from '@/modules/careers/types';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

interface JobApplicationPanelProps {
  page: CareersPageApi;
  job: CareersJobApi;
  allRolesPath: string;
}

/** The Apply card on a job's page: the form, then the confirmation. */
export function JobApplicationPanel({
  page,
  job,
  allRolesPath,
}: JobApplicationPanelProps) {
  const submitMutation = useSubmitJobApplicationMutation();

  if (submitMutation.isSuccess) {
    return (
      <JobApplicationSubmitted
        allRolesPath={allRolesPath}
        application={submitMutation.variables.body}
        companyName={page.companyName}
        jobTitle={job.title}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-[22px] leading-7 font-semibold tracking-[-0.02em]">
          Apply for this role
        </h2>
        <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
          <Timer aria-hidden className="size-3.5" />
          Takes about 2 minutes · No CV needed
        </p>
      </div>
      <JobApplicationForm
        companyName={page.companyName}
        error={
          submitMutation.isError
            ? actionErrorMessage('send your application', submitMutation.error)
            : null
        }
        isPending={submitMutation.isPending}
        onSubmit={(body) => submitMutation.mutate({ jobId: job.id, body })}
        privacyNoticeUrl={page.privacyNoticeUrl}
      />
    </div>
  );
}
